'use client'

/**
 * The reusable "point" primitive behind Point & Speak. A press-and-hold anywhere captures WHERE the user
 * pointed — as a stable semantic anchor when the element (or an ancestor) carries `data-anchor` /
 * `data-review-id`, otherwise a best-effort CSS selector + the visible text + coordinates. This is the asset:
 * once the anchor is captured, the user never has to describe *where* the issue is — only *what* it is.
 *
 * Deliberately framework-light (pointer events, no deps) so a second consumer (an "ask the AI about THIS"
 * mode) can reuse the exact same capture without touching the feedback UI.
 */

import { useEffect, useRef } from 'react'

export type AnchorContext = {
  route: string
  dataAnchor: string | null      // nearest [data-anchor]/[data-review-id] — the STABLE semantic id
  selector: string               // fallback CSS selector to the pointed element
  elementText: string            // the visible text at the point ("55", "82%", the sentence)
  anchorX: number
  anchorY: number
  viewportW: number
  viewportH: number
  appState: Record<string, unknown>
}

/**
 * Best-effort React component identity from the DOM node, by walking React's internal fiber tree (the same
 * source React DevTools reads). Returns the nearest NAMED component and, when available, its source file:line.
 * CAVEAT: in a MINIFIED production build component names are mangled (AnswerHero -> oZ) and _debugSource is
 * stripped, so this yields useful names mainly in dev/preview builds. It degrades to null silently; the stable
 * signal remains data-anchor (business-meaningful) + the selector. Wrapped in try/catch — never throws.
 */
function reactComponentInfo(el: Element | null): { component?: string; source?: string } {
  try {
    if (!el) return {}
    const key = Object.keys(el).find((k) => k.startsWith('__reactFiber$') || k.startsWith('__reactInternalInstance$'))
    if (!key) return {}
    let fiber = (el as unknown as Record<string, unknown>)[key] as
      | { type?: unknown; elementType?: unknown; return?: unknown; _debugSource?: { fileName?: string; lineNumber?: number } }
      | null
    let hops = 0
    while (fiber && hops < 30) {
      const t = (fiber.type ?? fiber.elementType) as { displayName?: string; name?: string } | string | undefined
      const name = typeof t === 'function' || (t && typeof t === 'object')
        ? ((t as { displayName?: string; name?: string }).displayName || (t as { name?: string }).name)
        : undefined
      // skip anonymous/minified 1-char names and intrinsic host tags (those are strings, handled by the selector)
      if (name && name.length > 1 && !/^[a-z]/.test(name)) {
        const src = fiber._debugSource
        const source = src?.fileName ? `${src.fileName.split('/').slice(-1)[0]}:${src.lineNumber ?? ''}` : undefined
        return { component: name, ...(source ? { source } : {}) }
      }
      fiber = fiber.return as typeof fiber
      hops++
    }
  } catch { /* fiber internals vary by React version — never let this break capture */ }
  return {}
}

/** Nearest ancestor (inclusive) that carries an explicit semantic anchor id, if any. */
function nearestSemanticAnchor(el: Element | null): string | null {
  let node: Element | null = el
  let hops = 0
  while (node && hops < 12) {
    const a = node.getAttribute?.('data-anchor') || node.getAttribute?.('data-review-id')
    if (a) return a
    node = node.parentElement
    hops++
  }
  return null
}

/** A short, reasonably-stable CSS selector for `el` (id > data-anchor > a 4-level nth-of-type path). */
export function buildSelector(el: Element | null): string {
  if (!el) return ''
  if (el.id) return `#${CSS.escape(el.id)}`
  const parts: string[] = []
  let node: Element | null = el
  let depth = 0
  while (node && node.nodeType === 1 && depth < 4 && node.tagName.toLowerCase() !== 'body') {
    let part = node.tagName.toLowerCase()
    const parent: Element | null = node.parentElement
    if (parent) {
      const sameTag = Array.from(parent.children).filter((c) => c.tagName === node!.tagName)
      if (sameTag.length > 1) part += `:nth-of-type(${sameTag.indexOf(node) + 1})`
    }
    parts.unshift(part)
    node = parent
    depth++
  }
  return parts.join(' > ')
}

/** Snapshot the anchor context at a point. `elementFromPoint` resolves what the finger/cursor is actually over. */
export function captureAnchor(x: number, y: number): AnchorContext {
  const el = (document.elementFromPoint(x, y) as Element | null) ?? document.body
  const text = ((el as HTMLElement).innerText ?? el.textContent ?? '').trim().replace(/\s+/g, ' ').slice(0, 300)
  // The app may expose a small reproduction snapshot on window.__MYCIO_REVIEW_STATE__ (current plan id, tab, etc.).
  const extra = (typeof window !== 'undefined'
    ? (window as unknown as { __MYCIO_REVIEW_STATE__?: Record<string, unknown> }).__MYCIO_REVIEW_STATE__
    : undefined) || {}
  return {
    route: typeof location !== 'undefined' ? location.pathname : '',
    dataAnchor: nearestSemanticAnchor(el),
    selector: buildSelector(el),
    elementText: text,
    anchorX: Math.round(x),
    anchorY: Math.round(y),
    viewportW: typeof window !== 'undefined' ? window.innerWidth : 0,
    viewportH: typeof window !== 'undefined' ? window.innerHeight : 0,
    // Fold the best-effort React component name + source into app_state (jsonb) — no schema change. In
    // dev/preview builds this gives triage "on <AnswerHero> (WealthOSPlanView.tsx:538)"; in minified prod it's
    // absent and data-anchor/selector carry the weight.
    appState: {
      pathname: typeof location !== 'undefined' ? location.pathname : '',
      title: typeof document !== 'undefined' ? document.title : '',
      ...reactComponentInfo(el),
      ...extra,
    },
  }
}

type LongPressOpts = {
  enabled: boolean
  onLongPress: (ctx: AnchorContext, clientX: number, clientY: number) => void
  holdMs?: number
  moveTolerance?: number
  // Desktop parity (step 3): a right-click (contextmenu) fires the SAME anchor capture as a touch long-press,
  // so "point at THIS on the plan and ask" works with a mouse too. Default off preserves the prior behavior
  // (contextmenu only suppressed after a touch long-press, never a trigger).
  rightClick?: boolean
}

/**
 * Document-wide press-and-hold detector. While `enabled`, holding for `holdMs` without moving past
 * `moveTolerance` fires `onLongPress` with the captured anchor. Movement (a scroll/drag) cancels it, so
 * scrolling never triggers a capture. On touch we suppress iOS's text-selection callout/magnifier
 * (`user-select`/`-webkit-touch-callout` off + contextmenu prevented) which would otherwise steal the gesture.
 */
export function useLongPressCapture({ enabled, onLongPress, holdMs = 550, moveTolerance = 10, rightClick = false }: LongPressOpts) {
  const cb = useRef(onLongPress)
  cb.current = onLongPress

  useEffect(() => {
    if (!enabled || typeof document === 'undefined') return
    let timer: ReturnType<typeof setTimeout> | null = null
    let startX = 0
    let startY = 0
    let fired = false

    // Selection suppression is scoped to an ACTIVE TOUCH hold only — NOT the whole armed lifetime, and never
    // for mouse. Setting body.userSelect='none' while merely armed made the ENTIRE page uncopyable (copy-paste
    // dead across MyCIO whenever Review was on). Desktop triggers via right-click (onContext) and never needs it;
    // touch needs it only during the hold so the gesture isn't hijacked by native selection / the iOS callout.
    const bs = document.body.style as unknown as { webkitTouchCallout?: string }
    let prevUserSelect = ''
    let prevCallout: string | undefined = ''
    const restoreSelection = () => { document.body.style.userSelect = prevUserSelect; bs.webkitTouchCallout = prevCallout || '' }
    const clear = () => { if (timer) { clearTimeout(timer); timer = null } restoreSelection() }

    const onDown = (e: PointerEvent) => {
      // Ignore presses that begin inside the review UI itself (popover/pill carry data-review-ui), and
      // inside charts ([data-chart]/.recharts-wrapper): a touch-hold on a chart belongs to the scrub
      // tooltip, and capturing there only yields a garbage concatenation of axis labels/data keys.
      if ((e.target as Element | null)?.closest?.('[data-review-ui],[data-ask-exclude],[data-chart],.recharts-wrapper')) return
      fired = false
      startX = e.clientX
      startY = e.clientY
      clear()
      if (e.pointerType === 'touch') {   // suppress native selection ONLY for the touch hold gesture
        prevUserSelect = document.body.style.userSelect
        prevCallout = bs.webkitTouchCallout
        document.body.style.userSelect = 'none'
        bs.webkitTouchCallout = 'none'
      }
      timer = setTimeout(() => {
        fired = true
        cb.current(captureAnchor(startX, startY), startX, startY)
      }, holdMs)
    }
    const onMove = (e: PointerEvent) => {
      if (!timer) return
      if (Math.abs(e.clientX - startX) > moveTolerance || Math.abs(e.clientY - startY) > moveTolerance) clear()
    }
    const onUp = () => clear()
    const onContext = (e: MouseEvent) => {
      if (fired) { e.preventDefault(); return }   // suppress the native menu right after a touch long-press
      if (!rightClick) return
      // Don't hijack a right-click inside the review UI itself (data-review-ui) or a chat surface
      // (data-ask-exclude): pinpointing is for the PLAN DOCUMENT, not the chat that answers it.
      if ((e.target as Element | null)?.closest?.('[data-review-ui],[data-ask-exclude],[data-chart],.recharts-wrapper')) return
      // Desktop equivalent of press-and-hold: capture what was right-clicked and open the ask surface there.
      e.preventDefault()
      cb.current(captureAnchor(e.clientX, e.clientY), e.clientX, e.clientY)
    }

    document.addEventListener('pointerdown', onDown, true)
    document.addEventListener('pointermove', onMove, true)
    document.addEventListener('pointerup', onUp, true)
    document.addEventListener('pointercancel', onUp, true)
    document.addEventListener('contextmenu', onContext, true)

    return () => {
      clear()   // restores selection if a touch hold was mid-flight
      document.removeEventListener('pointerdown', onDown, true)
      document.removeEventListener('pointermove', onMove, true)
      document.removeEventListener('pointerup', onUp, true)
      document.removeEventListener('pointercancel', onUp, true)
      document.removeEventListener('contextmenu', onContext, true)
    }
  }, [enabled, holdMs, moveTolerance, rightClick])
}
